import { Component, Inject, Input, NgZone, PLATFORM_ID, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { isPlatformBrowser } from '@angular/common';
import { ReportService } from 'src/app/reports/services/report.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from 'src/app/common/services/shared.service';

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent {
  lang!: number;
  private root: am5.Root | undefined;
  @Input() data: any = [];
  @Input() label: any = '';
  @Input() name: string = 'chartDiv';
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private zone: NgZone,
    private reportService: ReportService,
    private translate: TranslateService,
    private sharedService: SharedService
  ) { }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.root) {
      this.root.dispose();
    }
    this.createChart()
  }
  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          f();
        }, 1000)

      });
    }
  }
  createChart() {
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
      let root = am5.Root.new(this.name);
      root.setThemes([am5themes_Animated.new(root)]);

      let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          layout: root.verticalLayout
        })
      );

      const rawData = [
        { year: "2022", label: "Q3", value: 305.3 },
        { year: "2022", label: "Q4", value: 400.3 },
        { isSpacer: true },
        { year: "2023", label: "Q1", value: 505.3 },
        { year: "2023", label: "Q2", value: 555.1 },
        { year: "2023", label: "Q3", value: 600.3 },
        { year: "2023", label: "Q4", value: 600.8 },
        { isSpacer: true },
        { year: "2024", label: "Q1", value: 700.0 },
        { year: "2024", label: "Q2", value: 700.3 },
        { year: "2024", label: "Q3", value: 700.6 },
        { year: "2024", label: "Q4", value: 805.3 },
        { isSpacer: true },
        { year: "2025", label: "Q1", value: 900.0 },
        { year: "2025", label: "Q2", value: 900.3 }
      ];

      const labeledData = rawData.map((item, index) => {
        if (item.isSpacer) {
          return {
            category: `spacer-${index}`,
            displayLabel: "",
            group: "",
            isSpacer: true
          };
        }
        return {
          ...item,
          category: `Q${index + 1}`,
          displayLabel: item.label,
          group: item.year
        };
      });

      let xRenderer = am5xy.AxisRendererX.new(root, {
        minorGridEnabled: true,
        minGridDistance: 20
      });
      xRenderer.grid.template.set("visible", false);

      xRenderer.labels.template.setAll({
        oversizedBehavior: "wrap",
        text: "{displayLabel}",
        centerY: am5.p50,
        centerX: am5.p50,
        fontSize: 12
      });

      xRenderer.labels.template.adapters.add("text", function (text, target) {
        const dataItem: any = target.dataItem;
        if (dataItem && dataItem.dataContext.isSpacer) return "";
        return text;
      });

      let xAxis: any = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "category",
          renderer: xRenderer
        })
      );
      xAxis.data.setAll(labeledData);
      xAxis.set("groupFields", ["group"]);

      let yearRenderer = am5xy.AxisRendererX.new(root, {
        opposite: false,
        pan: "none",
        strokeOpacity: 0
      });
      yearRenderer.grid.template.set("visible", false);
      yearRenderer.labels.template.setAll({
        fontSize: 12,
        fontWeight: "bold",
        centerX: am5.p50,
        centerY: am5.p100
      });

      let yearAxis: any = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "group",
          renderer: yearRenderer,
          groupData: true
        })
      );

      const yearData = [...new Set(rawData.filter(d => !d.isSpacer).map(item => item.year))]
        .map(year => ({ group: year }));
      yearAxis.data.setAll(yearData);

      yearRenderer.labels.template.adapters.add("x", function (x, target: any) {
        if (!target.dataItem) return x;
        const group = target.dataItem.get("category");
        const groupItems = labeledData.filter(item => item.group === group);
        const positions = groupItems.map(item => {
          const dataItem = xAxis.dataItems.find((di: any) => di.get("category") === item.category);
          if (dataItem) {
            const start = xAxis.getDataItemCoordinateX(dataItem, "category", 0);
            const end = xAxis.getDataItemCoordinateX(dataItem, "category", 1);
            return (start + end) / 2;
          }
          return 0;
        }).filter(pos => pos !== 0);
        if (positions.length) {
          const minPos = Math.min(...positions);
          const maxPos = Math.max(...positions);
          return (minPos + maxPos) / 2;
        }
        return x;
      });

      let yRenderer = am5xy.AxisRendererY.new(root, {});
      yRenderer.grid.template.adapters.add("stroke", function (stroke, target: any) {
        const value = target.dataItem?.get("value");
        if (value === 0) {
          target.set("strokeOpacity", 1); // Full opacity
          return am5.color('#000'); // Black color
        } else {
          target.set("strokeOpacity", 0.3); // Lower opacity for other lines (optional)
          return am5.color(0xcccccc); // Default color
        }
      });

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
          min: 0 // Ensure grid line at 0 is included
        })
      );
      yAxis.get("renderer").labels.template.set("visible", false);

      let series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: this.label,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          categoryXField: "category",
          stroke: am5.color("#2372F0"), // Line color set here
          tooltip: am5.Tooltip.new(root, {
            labelText: "{valueY}"
          })
        })
      );

      series.data.setAll(labeledData.filter(item => !item.isSpacer));

      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 2.5,
            fill: am5.color('#2372F0')  // Red color bullet
          })
        });
      });

      series.bullets.push(function (root, series, dataItem: any) {
        return am5.Bullet.new(root, {
          locationY: 0,
          sprite: am5.Label.new(root, {
            text: dataItem.dataContext.value.toString(),
            centerY: am5.p50,
            centerX: am5.p50,
            dy: -20,
            fontSize: 9,
            fill: am5.color('#2372F0')
          })
        });
      });

      let legend = chart.children.push(am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50
      }));
      legend.data.setAll(chart.series.values);

      series.appear(1000);
      chart.appear(1000, 100);
    });
  }
}
